import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
} from '@mui/material';
import ShapeBackground from '../common/ShapeBackground';
import PlaidConnectModal from 'components/modals/PlaidConnectModal';
import { useSetPlaidAccessToken } from 'hooks/useSetPlaidAccessToken';
import { useListPlaidAccounts } from 'hooks/useListPlaidAccounts';
import { useDisbursementSettings } from 'hooks/useDisbursementSettings';
import { useUpdateDisbursementSettings } from 'hooks/useUpdateDisbursementSettings';
import DisbursementSettingsSection from './DisbursementSettingsSection';

const sidebarItems = [
  { label: 'Profile' },
  { label: 'Accounts' },
  { label: 'Disbursement Settings' },
];

const SettingsPage: React.FC = () => {
  const [selected, setSelected] = useState(1); // Default to 'Accounts'
  const [plaidModalOpen, setPlaidModalOpen] = useState(false);
  const { mutate: setPlaidAccessToken } = useSetPlaidAccessToken();
  const { data: plaidAccounts, isLoading } = useListPlaidAccounts();
  const { data: disbursementSettings } = useDisbursementSettings();
  useUpdateDisbursementSettings();

  // Controlled select state
  const [selectedSourceAccountId, setSelectedSourceAccountId] = useState<number | ''>('');
  useEffect(() => {
    if (disbursementSettings?.sourceAccount?.id) {
      setSelectedSourceAccountId(Number(disbursementSettings.sourceAccount.id));
    }
  }, [disbursementSettings]);

  // Debug logs
  useEffect(() => {
    console.log('disbursementSettings', disbursementSettings);
    console.log('plaidAccounts', plaidAccounts);
    console.log('selectedSourceAccountId', selectedSourceAccountId);
  }, [disbursementSettings, plaidAccounts, selectedSourceAccountId]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', pt: 8 }}>
      <ShapeBackground />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ display: 'flex', minHeight: 600, borderRadius: 3 }}>
          <Box sx={{ width: 220, borderRight: 1, borderColor: 'divider', py: 4 }}>
            <List>
              {sidebarItems.map((item, idx) => (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton selected={selected === idx} onClick={() => setSelected(idx)}>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: 1, p: 4, pt: 5 }}>
            {selected === 1 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Accounts
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ mb: 3, borderRadius: 2, fontWeight: 600, px: 3, py: 1 }}
                  onClick={() => setPlaidModalOpen(true)}
                >
                  Add Account
                </Button>
                <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={2}>Loading...</TableCell>
                        </TableRow>
                      ) : (
                        plaidAccounts?.map((acc) => (
                          <TableRow key={acc.id}>
                            <TableCell>
                              {acc.name}
                              {acc.official_name ? ` (${acc.official_name})` : ''}
                            </TableCell>
                            <TableCell>
                              {acc.mask ? `****${acc.mask}` : ''}
                              {acc.type ? ` • ${acc.type}` : ''}
                              {acc.subtype ? ` • ${acc.subtype}` : ''}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      {/* Empty rows for visual spacing, as in wireframe */}
                      <TableRow><TableCell colSpan={2}>&nbsp;</TableCell></TableRow>
                      <TableRow><TableCell colSpan={2}>&nbsp;</TableCell></TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {selected === 0 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Profile
                </Typography>
                {/* Profile content placeholder */}
                <Typography variant="body2" color="text.secondary">
                  Profile settings coming soon.
                </Typography>
              </>
            )}
            {selected === 2 && <DisbursementSettingsSection />}
          </Box>
        </Paper>
        <PlaidConnectModal
          open={plaidModalOpen}
          onClose={() => setPlaidModalOpen(false)}
          onPlaidSuccess={(publicToken, metadata) => setPlaidAccessToken(publicToken)}
        />
      </Container>
    </Box>
  );
};

export default SettingsPage; 